import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Snackbar, Alert, Checkbox, FormControlLabel, Grid, Divider
} from '@mui/material';
import { Add, Delete, EventAvailable } from '@mui/icons-material';
import axios from 'axios';
import AvailabilityGantt from './AvailabilityGantt'; // Подключаем шахматку

const DAYS = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];

const AvailabilityCalendar: React.FC = () => {
  const [guides, setGuides] = useState<any[]>([]);
  const [selectedGuide, setSelectedGuide] = useState<number>(0);
  const [weekly, setWeekly] = useState<any[]>([]);
  const [exceptions, setExceptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Модалка исключений
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({ date: '', time_from: '10:00', time_to: '23:59', is_available: true });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    axios.get('/api/guides/active').then(res => {
      setGuides(res.data);
      if (res.data.length > 0) setSelectedGuide(res.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (selectedGuide) fetchData(selectedGuide);
  }, [selectedGuide]);

  const fetchData = async (guideId: number) => {
    setLoading(true);
    try {
      const [wRes, eRes] = await Promise.all([
        axios.get(`/api/guide-availability/weekly/${guideId}`),
        axios.get(`/api/guide-availability/${guideId}`)
      ]);
      
      // Если время null, подставляем дефолтные значения, чтобы инпуты не были пустыми
      const normalizedWeekly = wRes.data.map((day: any) => ({
        ...day,
        time_from: day.time_from || '10:00',
        time_to: day.time_to || '18:00',
        is_available: Boolean(day.is_available)
      }));
      
      setWeekly(normalizedWeekly);
      setExceptions(eRes.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleWeeklyChange = (dayIndex: number, field: string, value: any) => {
    const dayOfWeek = dayIndex + 1;
    const existing = weekly.find(w => w.day_of_week === dayOfWeek);
    
    const fallback = { 
      day_of_week: dayOfWeek, 
      is_available: false, 
      time_from: '10:00', 
      time_to: '18:00' 
    };
    
    const updated = { ...(existing || fallback), [field]: value };
    setWeekly(weekly.filter(w => w.day_of_week !== dayOfWeek).concat(updated));
  };

  const saveWeekly = async () => {
    try {
      await axios.post(`/api/guide-availability/weekly/${selectedGuide}`, { schedule: weekly });
      setSnackbar({ open: true, message: 'Шаблон сохранен', severity: 'success' });
    } catch (err) { setSnackbar({ open: true, message: 'Ошибка', severity: 'error' }); }
  };

  const handleAddException = async () => {
    try {
      await axios.post(`/api/guide-availability/${selectedGuide}`, formData);
      setOpenModal(false);
      fetchData(selectedGuide);
      setSnackbar({ open: true, message: 'Исключение добавлено', severity: 'success' });
    } catch (err) { setSnackbar({ open: true, message: 'Ошибка', severity: 'error' }); }
  };

  const handleDeleteException = async (id: number) => {
    if (!window.confirm('Удалить исключение?')) return;
    try {
      await axios.delete(`/api/guide-availability/${id}`);
      fetchData(selectedGuide);
    } catch (err) { alert('Ошибка'); }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <EventAvailable sx={{ fontSize: 32, color: 'primary.main' }} />
        
      </Box>

      {/* НОВЫЙ БЛОК: Шахматка */}
      <AvailabilityGantt />

      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControl sx={{ minWidth: 250 }}>
          <InputLabel>Экскурсовод</InputLabel>
          <Select value={selectedGuide} label="Экскурсовод" onChange={(e) => setSelectedGuide(Number(e.target.value))}>
            {guides.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Paper>

      <Grid container spacing={3}>
        {/* Шаблон недели */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Шаблон недели</Typography>
            <Typography variant="body2" color="textSecondary" mb={2}>Действует по умолчанию каждый месяц</Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {DAYS.map((day, idx) => {
                const dayData = weekly.find(w => w.day_of_week === idx + 1) || { is_available: false, time_from: '10:00', time_to: '14:00', time_from_2: '15:00', time_to_2: '19:00' };
                return (
                  <Box key={day} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, pb: 1.5, borderBottom: '1px dashed #e0e0e0' }}>
                    <FormControlLabel
                      control={<Checkbox size="small" checked={!!dayData.is_available} onChange={(e) => handleWeeklyChange(idx, 'is_available', e.target.checked)} />}
                      label={<Typography variant="body2" sx={{ fontWeight: dayData.is_available ? 600 : 400, minWidth: 100 }}>{day}</Typography>}
                      sx={{ m: 0 }}
                    />
                    
                    {dayData.is_available ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#f5f7fa', p: 0.5, borderRadius: 1 }}>
                          
                          <TextField type="time" size="small" value={dayData.time_from || '10:00'} onChange={(e) => handleWeeklyChange(idx, 'time_from', e.target.value)} sx={{ '& input': { py: 0.5, px: 0.5, fontSize: '0.85rem' }, bgcolor: 'white', borderRadius: 1 }} />
                          <Typography variant="body2" color="textSecondary">—</Typography>
                          <TextField type="time" size="small" value={dayData.time_to || '14:00'} onChange={(e) => handleWeeklyChange(idx, 'time_to', e.target.value)} sx={{ '& input': { py: 0.5, px: 0.5, fontSize: '0.85rem' }, bgcolor: 'white', borderRadius: 1 }} />
                        </Box>
                        

                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', pr: 1 }}>
                        Выходной
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
            <Button variant="contained" onClick={saveWeekly} sx={{ mt: 2 }}>Сохранить шаблон</Button>
          </Paper>
        </Grid>

        {/* Исключения */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Box>
                <Typography variant="h6">Исключения на даты</Typography>
                <Typography variant="body2" color="textSecondary">Перекрывают шаблон на конкретный день</Typography>
              </Box>
              <Button variant="outlined" startIcon={<Add />} onClick={() => {
                setFormData({ date: '', time_from: '10:00', time_to: '23:59', is_available: true });
                setOpenModal(true);
              }}>Добавить</Button>
            </Box>

            {exceptions.length === 0 ? <Typography color="textSecondary">Нет исключений</Typography> : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Дата</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Время</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {exceptions.map(ex => (
                      <TableRow key={ex.id}>
                        <TableCell>{new Date(ex.date).toLocaleDateString('ru-RU')}</TableCell>
                        <TableCell>{ex.is_available ? '✅ Доступен' : '❌ Недоступен'}</TableCell>
                        <TableCell>{ex.is_available ? `${ex.time_from?.slice(0,5)} - ${ex.time_to?.slice(0,5)}` : '-'}</TableCell>
                        <TableCell align="right">
                          <IconButton color="error" size="small" onClick={() => handleDeleteException(ex.id)}><Delete /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Добавить исключение</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Дата" type="date" InputLabelProps={{ shrink: true }} value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} />
            <FormControlLabel
              control={<Checkbox checked={formData.is_available} onChange={(e) => setFormData({ ...formData, is_available: e.target.checked })} />}
              label="Доступен в этот день (сверх шаблона)"
            />
            {formData.is_available && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField label="С" type="time" InputLabelProps={{ shrink: true }} value={formData.time_from} onChange={(e) => setFormData({ ...formData, time_from: e.target.value })} fullWidth />
                <TextField label="По" type="time" InputLabelProps={{ shrink: true }} value={formData.time_to} onChange={(e) => setFormData({ ...formData, time_to: e.target.value })} fullWidth />
              </Box>
            )}
            {!formData.is_available && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                Гид будет отмечен как недоступный на весь день.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Отмена</Button>
          <Button onClick={handleAddException} variant="contained">Сохранить</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AvailabilityCalendar;
